import { api } from '@/api/controllers/api';
import { IProxyPhoneDto } from '@/models';
import { handleSagaError } from '@/store/common';
import { EMessageType } from '@/store/messaging';
import { messageFactory } from '@/store/messaging';
import { call, put, takeLatest } from 'redux-saga/effects';
import { actionCreator } from '.';

export function* saga() {
	yield takeLatest(
		actionCreator.LOAD_PROXY_PHONES_START,
		onLoadProxyPhonesStart
	);
	yield takeLatest(
		actionCreator.SYNC_PROXY_PHONES_START,
		onSyncProxyPhonesStart
	);
	yield takeLatest(
		actionCreator.UNLOCK_PROXY_PHONE_START,
		onUnlockProxyPhoneStart
	);
}

function* onLoadProxyPhonesStart() {
	try {
		yield put(messageFactory.progress('Loading proxy phones...'));
		const response: IProxyPhoneDto[] = yield call(
			api.proxyPhones.getProxyPhones
		);
		yield put(actionCreator.LOAD_PROXY_PHONES_COMPLETED(response));
	} catch (error) {
		yield handleSagaError(error, [
			actionCreator.LOAD_PROXY_PHONES_FAILED()
		]);
	} finally {
		yield put(messageFactory.dismissProgress());
	}
}

function* onSyncProxyPhonesStart(
	action: ReturnType<typeof actionCreator.SYNC_PROXY_PHONES_START>
) {
	try {
		const company: string = action.payload;
		if (!company) {
			throw new Error('Select a company before syncing.');
		}

		yield put(
			messageFactory.progress(`Syncing proxy phones for ${company}...`)
		);
		const modified: number = yield call(
			api.proxyPhones.postSyncProxyPhones,
			company
		);

		if (modified > 0) {
			const response: IProxyPhoneDto[] = yield call(
				api.proxyPhones.getProxyPhones
			);
			yield put(actionCreator.LOAD_PROXY_PHONES_COMPLETED(response));
		} else {
			yield put(
				messageFactory.message(
					EMessageType.INFO,
					'Sync complete',
					'No proxy phones were synced.'
				)
			);
		}

		yield put(actionCreator.SYNC_PROXY_PHONES_COMPLETED());
	} catch (error) {
		yield handleSagaError(error, [
			actionCreator.SYNC_PROXY_PHONES_FAILED()
		]);
	} finally {
		yield put(messageFactory.dismissProgress());
	}
}

function* onUnlockProxyPhoneStart(
	action: ReturnType<typeof actionCreator.UNLOCK_PROXY_PHONE_START>
) {
	try {
		const proxyPhone: string = action.payload;
		if (!proxyPhone) {
			throw new Error('Select a proxy phone before unlocking.');
		}

		yield put(messageFactory.progress(`Unlocking ${proxyPhone}...`));
		yield call(api.proxyPhones.postUnlockProxyPhone, proxyPhone);
		yield put(actionCreator.UNLOCK_PROXY_PHONE_COMPLETED(proxyPhone));
	} catch (error) {
		yield handleSagaError(error, [
			actionCreator.UNLOCK_PROXY_PHONE_FAILED()
		]);
	} finally {
		yield put(messageFactory.dismissProgress());
	}
}
